import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response,
    Pagination
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { Money, PayablesInput, PayablesResponse, Query } from '../../model/models';

export interface PaymentDetailDto {
    buyerPaidFeesEnabled?: boolean;
    fee?: Money;
    feeRate?: number;
    group?: boolean;
    invoicePayment?: Money;
    paymentDate?: any;
    paymentStatus?:
        | 'UNKNOWN_FUNDING_STATUS'
        | 'STAGED'
        | 'REJECTED'
        | 'SENT'
        | 'IN_TRANSFER'
        | 'CANCELLED'
        | 'SCHEDULED'
        | 'PAID'
        | 'UNRECOGNIZED'
        | 'APPROVED';
}

export class PaymentDetailRequest extends Mk2Request {
    invoiceKey?: string;
    isPayerLSQ?: boolean;
    message?: string;
    ok?: boolean;

    constructor(initial?: Partial<PaymentDetailRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class PaymentDetailResponse extends Mk2Response {
    pagination?: Pagination;
    rows?: PaymentDetailDto[];
    totalCount?: number;

    constructor(initial?: Partial<PaymentDetailResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class GetPaymentDetailService extends ApiService<
    PaymentDetailRequest,
    PaymentDetailResponse
> {
    public requestType = PaymentDetailRequest;
    public responseType = PaymentDetailResponse;
    public endpointName = 'BuyerAccount/GetPaymentDetail/GetPaymentDetail';
    public serviceName = 'financing';
    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService,
        private _userService: UserService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'BuyerAccount/GetPaymentDetail/GetPaymentDetail';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(req: PaymentDetailRequest): { query: string } | PaymentDetailRequest {
        if (this._isPayLaterEnabled) {
            const input: PayablesInput = {
                payableIds: [+req.invoiceKey],
                companyFilter: { buyerIds: [this._userService.getAlternateCompanyKey()] }
            };

            const query: Query = {
                payables: {
                    __args: { input },
                    // TODO: Add missing fields
                    rows: { transfers: { traceNumber: true } }
                }
            };

            return {
                query: `{${jsonToGraphQLQuery(query)}}`
            };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | PaymentDetailResponse
    ): PaymentDetailResponse {
        if (this._isPayLaterEnabled) {
            // TODO: Implement mapping
            return new PaymentDetailResponse({
                ok: true,
                totalCount: 0,
                rows: []
            });
        }
        return res as PaymentDetailResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
